import { useEffect, useMemo, useState } from 'react';
import { marketDataRuntime } from '../../data/client/marketDataRuntime';
import { newsStore, useNewsItems } from '../../data/stores/newsStore';
import { useQuoteUniverse } from '../../data/stores/quoteStore';
import type { NewsWindowConfig, WindowConfig, WindowInstance } from '../../workspace/types';
import { Select } from '../../ui/Select/Select';
import { Button } from '../../ui/Button/Button';
import { formatTime } from '../../shared/format';

interface NewsWindowProps {
  window: WindowInstance<NewsWindowConfig>;
  onConfigChange: (updater: (config: WindowConfig) => WindowConfig) => void;
  onOpenNewsDetail: (newsId: string) => void;
}

export function NewsWindow({ window, onConfigChange, onOpenNewsDetail }: NewsWindowProps) {
  const universe = useQuoteUniverse();
  const items = useNewsItems();
  const [query, setQuery] = useState('');
  const selectedSymbol = window.config.symbols[0] ?? '';

  useEffect(() => {
    return marketDataRuntime.subscribeNews(window.id, window.config.symbols, window.config.limit);
  }, [window.id, window.config.symbols, window.config.limit]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((item) => window.config.showUnreadOnly ? !item.read : true)
      .filter((item) => selectedSymbol ? item.symbols.includes(selectedSymbol) : true)
      .filter((item) => q ? `${item.headline} ${item.summary} ${item.body} ${item.symbols.join(' ')}`.toLowerCase().includes(q) : true)
      .slice(0, window.config.limit);
  }, [items, query, selectedSymbol, window.config.showUnreadOnly, window.config.limit]);

  return (
    <div className="news-window window-content">
      <div className="window-toolbar no-window-drag">
        <input
          className="terminal-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search news"
          aria-label="Search news"
        />
        <Select
          label="Symbol"
          value={selectedSymbol}
          onChange={(event) => onConfigChange((config) => ({ ...config, symbols: event.target.value ? [event.target.value] : [] } as NewsWindowConfig))}
        >
          <option value="">All</option>
          {(universe.length ? universe.slice(0, 180) : []).map((symbol) => <option key={symbol} value={symbol}>{symbol}</option>)}
        </Select>
        <Button
          variant={window.config.showUnreadOnly ? 'primary' : 'secondary'}
          onClick={() => onConfigChange((config) => ({ ...config, showUnreadOnly: !(config as NewsWindowConfig).showUnreadOnly } as NewsWindowConfig))}
        >
          Unread
        </Button>
        <span className="toolbar-stat">items {filtered.length}</span>
      </div>
      <div className="news-list no-window-drag">
        {filtered.map((item) => (
          <article
            key={item.id}
            className={`news-card news-card--${item.priority} ${item.read ? 'news-card--read' : ''}`}
            onClick={() => {
              newsStore.markRead(item.id, true);
              onOpenNewsDetail(item.id);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                newsStore.markRead(item.id, true);
                onOpenNewsDetail(item.id);
              }
            }}
            title="Open full story"
          >
            <header>
              <span>{formatTime(item.time)}</span>
              <strong>{item.source}</strong>
              <small>{item.symbols.join(' ')}</small>
            </header>
            <h3>{item.headline}</h3>
            <p>{item.summary}</p>
          </article>
        ))}
        {filtered.length === 0 ? <div className="empty-state">No matching news.</div> : null}
      </div>
    </div>
  );
}
